// Impact stats computed from the publicWinners projection.
//
// publicWinners is the public mirror of every grant winner (see
// syncPublicWinner in functions/index.js): published winners in full, hidden
// winners as redacted aggregate-only stubs. `pitches` is reviewer-gated.
// Chapter pages show one chapter's numbers; the landing surfaces total all
// chapters. Both go through fetchImpactStats so the counting rules stay
// identical.

import { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { resolveAwardAmount, chaptersByNameMap } from "./awardAmount";
import { IMPACT_STATS } from "../data/landingContent";

// The pitch form's local state uses `selfId` but persists to Firestore as
// `selfIdentification` (see PitchPage.jsx / googleSheets.js); legacy imports
// may also use `selfId`, so read either. Values arrive as an array or a
// comma-joined string depending on import vintage; match case-insensitive
// substrings so "Women-Owned", "Women Owned/Led", "BIPOC-owned" all count
// the same.
function selfIdText(data) {
  const raw = data.selfIdentification ?? data.selfId;
  return Array.isArray(raw) ? raw.join(", ") : String(raw || "");
}

// Aggregate published winners into impact stats. With chapterName, only that
// chapter's winners count; without it, every chapter's do.
//
// Chapters load alongside the winners so award amounts resolve through the
// same cascade the admin tabs use (awardAmount → chapter.defaultGrantAmount →
// historical $1,000). Both collections are world-readable. Without the
// chapters map, winners that predate the awardAmount field would count $1,000
// publicly even in a chapter whose default grant is larger — a silent
// admin-vs-public totals mismatch.
export async function fetchImpactStats(chapterName) {
  const [snap, chapterSnap] = await Promise.all([
    getDocs(collection(db, "publicWinners")),
    getDocs(collection(db, "chapters")),
  ]);
  const chaptersByName = chaptersByNameMap(chapterSnap.docs.map((d) => d.data()));
  let grants = 0, dollars = 0, women = 0, bipoc = 0;
  snap.docs.forEach((d) => {
    const data = d.data() || {};
    // Winners staged in the admin Grant awards tab but not yet published
    // arrive as redacted stubs (chapter, amount, self-ID only — see
    // syncPublicWinner). They COUNT here: the money is awarded whether or not
    // the winner is announced yet. Only the display surfaces (awardees grid,
    // founder map, mobile landing) filter winnerPublished === false.
    if (chapterName && (data.chapter || "") !== chapterName) return;
    grants += 1;
    dollars += resolveAwardAmount(data, chaptersByName);
    const ids = selfIdText(data);
    if (/women/i.test(ids)) women += 1;
    if (/bipoc/i.test(ids)) bipoc += 1;
  });
  return {
    grants,
    dollars,
    womenPct: grants > 0 ? Math.round((women / grants) * 100) : 0,
    bipocPct: grants > 0 ? Math.round((bipoc / grants) * 100) : 0,
  };
}

// All-chapter totals in the {n, label} shape the landing surfaces render,
// with the hardcoded IMPACT_STATS copy as the value until the live numbers
// arrive (and if the fetch fails), so the section never renders empty.
export function useLandingImpactStats() {
  const [stats, setStats] = useState(IMPACT_STATS);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const live = await fetchImpactStats();
        if (cancelled || live.grants <= 0) return;
        const [funded, women, bipoc, awarded] = IMPACT_STATS;
        setStats([
          { n: String(live.grants), label: funded.label },
          { n: `${live.womenPct}%`, label: women.label },
          { n: `${live.bipocPct}%`, label: bipoc.label },
          { n: `$${Math.floor(live.dollars / 1000)}K${live.dollars % 1000 > 0 ? "+" : ""}`, label: awarded.label },
        ]);
      } catch (e) {
        // Keep the hardcoded copy.
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return stats;
}
